import { t } from 'i18next';
import { DoubleSide, Points } from 'three';

import { ContextMenuItem } from '../../../../../../react/components/skyview/context-menu';
import { getNextDefaultName } from '../../../../../../react/hooks/use-default-asset-name';
import { isDefined } from '../../../../../../utils/variables';
import cameraManager from '../../../../../camera-manager';
import {
  findLineSegmentIdFromIntersection,
  LineSegmentId,
} from '../../../../../elements/design/design-node/utils/line-segment-node';
import { InputHandler } from '../../../../../input-handler';
import { publish, SubscriptionTopic } from '../../../../../messaging/pubsub';
import { MouseInputData } from '../../../../../utilities/input-event/input-event-data-extractor';
import {
  ActionInProgress,
  ActionName,
  DesignMode,
  DesignTool,
  DesignToolDialogState,
} from '../../../../design-tool';
import { option } from '../../commands';
import { findSmallestPolygonOrLongestPolyline } from '../utils/multi-select';
import {
  applySelectionResult,
  applySelectLineOrPolygon,
  deselect,
  getMultiselectResult,
} from '../utils/select';
import { gridFunctions } from './features/generate-grid';
import { lineSegmentFunctions } from './features/generate-line-segments';
import { importFunctions } from './features/import-geometry';
import { interpolatePointHeightsUtils } from './features/interpolate-point-heights-utils';
import { offsetUtils } from './features/offset-utils';
import { pointHeightFunctions } from './features/set-point-heights';
import { triangulateFunctions } from './features/triangulate';
import { createLine } from './line/create';
import { createPoint } from './point/create';
import {
  openCreatePointByCoordinateDialog,
  openCreatePointByOffsetDialog,
} from './point/create-from-dialog';
import { createTextAction } from './text/create';

/**
 * Identifies a line or polygon and appends it to the multiselect.
 */
export const selectLineOrPolygon = (tool: DesignTool): ContextMenuItem => {
  const startLineSegmentId = tool.intersection?.index!;
  const disabled = !isDefined(startLineSegmentId);

  const alreadyIncluded = tool.currentSelection.lineSegments.some((x) => x === startLineSegmentId);

  return {
    title: alreadyIncluded
      ? t('contextMenu.deselectLinePolygon', { ns: 'skyviewDesign' })
      : t('contextMenu.selectLinePolygon', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      const result = getShortestLineOrPolygonFromIntersection(
        tool,
        alreadyIncluded ? tool.currentSelection.lineSegments : undefined,
      );
      applySelectLineOrPolygon(tool, result.lines, alreadyIncluded ? 'deselect' : 'select');
      tool.contextMenuCallback?.();
    },
    disabled,
  };
};

const getShortestLineOrPolygonFromIntersection = (tool: DesignTool, filter?: LineSegmentId[]) => {
  const lineSegmentId = findLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection);

  if (!isDefined(lineSegmentId)) {
    throw new Error('Error selecting line or polygon, no intersection found');
  }

  return findSmallestPolygonOrLongestPolyline(tool.asset.designNode, lineSegmentId, filter);
};

export const quickSelectLineOrPolygon = (tool: DesignTool) => {
  const result = getShortestLineOrPolygonFromIntersection(tool);

  const allLinesSelected = result.lines.every((x) =>
    isDefined(tool.currentSelection.lineSegments.find((y) => x.id === y)),
  );

  applySelectLineOrPolygon(tool, result.lines, allLinesSelected ? 'deselect' : 'select');
};

/**
 * Selects all geometries.
 */
export const selectAll = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.selectAll', { ns: 'skyviewDesign' }),
    action: () => {
      const selectionResult = getMultiselectResult(tool, tool.asset.designNode.points);
      applySelectionResult(tool, selectionResult);
      tool.contextMenuCallback?.();
    },
  };
};

/**
 * Deselects all geometry selected with multiselect
 */
export const deselectAll = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.deselectAll', { ns: 'skyviewDesign' }),
    action: () => {
      deselect(tool);
      tool.contextMenuCallback?.();
    },
    hotkey: 'Esc',
    disabled: !tool.multiSelectInProgress,
  };
};

export const undo = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.undo', { ns: 'skyviewDesign' }),
    action: () => {
      const lastAction = tool.actions.pop();
      if (lastAction) {
        lastAction.rollback();
        tool.onEdited?.();
        tool.undoActions.push(lastAction);
      }
      tool.contextMenuCallback?.();
    },
    hotkey: 'Ctrl + Z',
    disabled: tool.actions.length === 0,
  };
};

export const redo = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.redo', { ns: 'skyviewDesign' }),
    action: () => {
      const lastUndo = tool.undoActions.pop();
      if (lastUndo) {
        lastUndo.commit();
        tool.onEdited?.();
        tool.actions.push(lastUndo);
      }

      tool.contextMenuCallback?.();
      return Promise.resolve();
    },
    hotkey: 'Ctrl + Y',
    disabled: tool.undoActions.length === 0,
  };
};

export const showFunctions = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.text', { ns: 'skyviewDesign' }),
    action: () => {
      /* Do nothing */
    },
    children: [
      triangulateFunctions.activateTriangualateFeature(tool),
      gridFunctions.activateGenerateGridFeature(tool),
      interpolatePointHeightsUtils.activateInterpolatePointHeightsFeature(tool),
      lineSegmentFunctions.activateGenerateLineSegmentsFeature(tool),
      pointHeightFunctions.activateSetPointHeightsFeature(tool),
      offsetUtils.activateCreateOffsetFeature(tool),
      importFunctions.importPointsFromCsv(tool),
    ],
  };
};

export const showOptions = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.options.text', { ns: 'skyviewDesign' }),
    action: () => {
      /* Do nothing */
    },
    children: [
      {
        title: t('contextMenu.options.items.focusGeometry', { ns: 'skyviewDesign' }),
        action: () => {
          cameraManager().getActiveController()!.focusOnAsset(tool.asset);
        },
        disabled: tool.asset.designNode.pointNodeCount === 0,
      },
      {
        title: t('contextMenu.options.items.doubleSided', { ns: 'skyviewDesign' }),
        action: () => {
          tool.asset.toggleDoubleSided();
        },
        toggled: tool.asset.side === DoubleSide,
      },
      {
        title: t('contextMenu.options.items.selection.text', { ns: 'skyviewDesign' }),
        action: async () => {
          // return Promise.resolve();
        },
        children: [
          {
            title: t('contextMenu.options.items.selection.items.points', { ns: 'skyviewDesign' }),
            action: () => {
              tool.setSelectOptions({ points: !tool.selectOptions.points });
              return Promise.resolve();
            },
            toggled: tool.selectOptions.points,
          },
          {
            title: t('contextMenu.options.items.selection.items.lines', { ns: 'skyviewDesign' }),
            action: () => {
              tool.setSelectOptions({ lines: !tool.selectOptions.lines });
              return Promise.resolve();
            },
            toggled: tool.selectOptions.lines,
          },
          {
            title: t('contextMenu.options.items.selection.items.breakLines', {
              ns: 'skyviewDesign',
            }),
            action: () => {
              tool.setSelectOptions({ breakLines: !tool.selectOptions.breakLines });
              return Promise.resolve();
            },
            toggled: tool.selectOptions.breakLines,
          },
          {
            title: t('contextMenu.options.items.selection.items.triangles', {
              ns: 'skyviewDesign',
            }),
            action: () => {
              tool.setSelectOptions({ faces: !tool.selectOptions.faces });
              return Promise.resolve();
            },
            toggled: tool.selectOptions.faces,
          },
        ],
      },
    ],
  };
};

export const tools = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.tools.text', { ns: 'skyviewDesign' }),
    action: () => {
      /* Do nothing */
    },
    children: [
      {
        title: t('contextMenu.tools.items.line', { ns: 'skyviewDesign' }),
        action: () => {
          tool.designMode = DesignMode.Line;
          tool.contextMenuCallback?.();
        },
        hotkey: 'Q',
      },
      {
        title: t('contextMenu.tools.items.point', { ns: 'skyviewDesign' }),
        action: () => {
          tool.designMode = DesignMode.Point;
          tool.contextMenuCallback?.();
        },
        hotkey: 'W',
      },
      ...option(tool.designCurves, () => ({
        title: t('contextMenu.tools.items.curve', { ns: 'skyviewDesign' }),
        action: () => {
          tool.designMode = DesignMode.Curve;
          tool.contextMenuCallback?.();
        },
        hotkey: 'U',
      })),
      ...option(tool.designCurves, () => ({
        title: t('contextMenu.tools.items.circle', { ns: 'skyviewDesign' }),
        action: () => {
          tool.designMode = DesignMode.Circle;
          tool.contextMenuCallback?.();
        },
        hotkey: 'C',
      })),
      {
        title: t('contextMenu.tools.items.select', { ns: 'skyviewDesign' }),
        action: () => {
          tool.designMode = DesignMode.Select;
          tool.contextMenuCallback?.();
        },
        hotkey: 'E',
      },
      {
        title: t('section.tools.textButton.text', { ns: 'skyviewDesign' }),
        action: () => {
          tool.designMode = DesignMode.Text;
          tool.contextMenuCallback?.();
        },
        hotkey: 'R',
      },
    ],
  };
};

/**
 * Initiate a new action in progress.
 * If another action is already active the other action will be aborted in favor of the new one.
 */
export function initActionInProgress(tool: DesignTool, action: ActionInProgress) {
  if (isDefined(tool.actionInProgress)) {
    abortActionInProgressAction(tool);
  }

  tool.actionInProgress = action;
  action.initiate?.();
  publish(SubscriptionTopic.DesignToolActiveActionChanged, undefined);
}

export const applyActionInProgress = (tool: DesignTool): ContextMenuItem => {
  const resolveTitle = () => {
    switch (tool.actionInProgress!.actionName) {
      case ActionName.CreateLine:
        return t('contextMenu.completeCreateLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.MovePoint:
        return t('contextMenu.completeMovePointAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.Offset:
        return t('contextMenu.completeOffsetAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.InterpolatePointHeights:
        return t('contextMenu.completeInterpolatePointHeightsAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.SelectPolyLine:
        return t('contextMenu.completeSelectPolylineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.SetHeight:
        return t('contextMenu.completeSetHeightAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.Triangulate:
        return t('contextMenu.completeTriangulateAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateText:
        return t('contextMenu.completeCreateLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateCurve:
        return t('contextMenu.completeCreateCurveAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.BendCurve:
        return t('contextMenu.completeBendLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateCircle:
        return t('contextMenu.completeCreateCircleAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.ResizeCircle:
        return t('contextMenu.completeResizeCircleAction', {
          ns: 'skyviewDesign',
        });
    }
  };

  return {
    title: resolveTitle(),
    action: (e?: MouseInputData) => {
      tool.actionInProgress?.cleanup?.();
      tool.actionInProgress?.apply(e);
      tool.actionInProgress = undefined;
      publish(SubscriptionTopic.DesignToolActiveActionChanged, undefined);

      tool.cursorLine.visible = false;
      tool.cursorFace.visible = false;
      tool.contextMenuCallback?.();
    },
    disabled:
      tool.actionInProgress?.actionName === ActionName.MovePoint && tool.intersection !== undefined,
  };
};

export const abortActionInProgressAffirmative = (tool: DesignTool): ContextMenuItem => {
  const resolveTitle = () => {
    switch (tool.actionInProgress!.actionName) {
      case ActionName.CreateLine:
        return t('contextMenu.endCreateLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.MovePoint:
        return t('contextMenu.endMovePointAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.Offset:
        return t('contextMenu.endOffsetAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.InterpolatePointHeights:
        return t('contextMenu.endInterpolatePointHeightsAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.SelectPolyLine:
        return t('contextMenu.cancelSelectPolylineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.SetHeight:
        return t('contextMenu.endSetHeightAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.Triangulate:
        return t('contextMenu.endTriangulateAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateText:
        return t('contextMenu.endCreateLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateCurve:
        return t('contextMenu.endCreateCurveAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.BendCurve:
        return t('contextMenu.endBendLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateCircle:
        return t('contextMenu.endCreateCircleAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.ResizeCircle:
        return t('contextMenu.endResizeCircleAction', {
          ns: 'skyviewDesign',
        });
    }
  };

  return {
    title: resolveTitle(),
    action: async () => {
      await abortActionInProgress(tool).action();
    },
    hotkey: 'Esc',
  };
};

export const abortActionInProgress = (tool: DesignTool): ContextMenuItem => {
  const resolveTitle = () => {
    switch (tool.actionInProgress!.actionName) {
      case ActionName.CreateLine:
        return t('contextMenu.cancelCreateLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.MovePoint:
        return t('contextMenu.cancelMovePointAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.Offset:
        return t('contextMenu.cancelOffsetAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.InterpolatePointHeights:
        return t('contextMenu.cancelInterpolatePointHeightsAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.SelectPolyLine:
        return t('contextMenu.cancelSelectPolylineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.SetHeight:
        return t('contextMenu.cancelSetHeightAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.Triangulate:
        return t('contextMenu.cancelTriangulateAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateText:
        return t('contextMenu.cancelCreateLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateCurve:
        return t('contextMenu.cancelCreateCurveAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.BendCurve:
        return t('contextMenu.cancelBendLineAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.CreateCircle:
        return t('contextMenu.cancelCreateCircleAction', {
          ns: 'skyviewDesign',
        });
      case ActionName.ResizeCircle:
        return t('contextMenu.cancelResizeCircleAction', {
          ns: 'skyviewDesign',
        });
    }
  };

  return {
    title: resolveTitle(),
    action: () => {
      abortActionInProgressAction(tool);
      publish(SubscriptionTopic.DesignToolActiveActionChanged, undefined);
      tool.contextMenuCallback?.();
    },
    hotkey: 'Esc',
  };
};

function abortActionInProgressAction(tool: DesignTool) {
  tool.actionInProgress?.cleanup?.();
  tool.actionInProgress = undefined;
}

export const saveTool = (tool: DesignTool) => {
  tool.uiState =
    isDefined(tool.asset.assetId) && tool.asset.name !== getNextDefaultName('design')
      ? DesignToolDialogState.Saving
      : DesignToolDialogState.SetName;
};

export const abortTool = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.completeEdit', { ns: 'skyviewDesign' }),
    action: () => {
      InputHandler.instance.setTool('select');
      tool.contextMenuCallback?.();
    },
    hotkey: 'Esc',
  };
};

export const createActions = (tool: DesignTool): ContextMenuItem => {
  // Creating a line or text would abort the line that is being created.
  const creatingLine = tool.actionInProgress?.actionName === ActionName.CreateLine;

  return {
    title: t('contextMenu.create.text', { ns: 'skyviewDesign' }),
    children: [
      ...option(!creatingLine, () => createLine(tool)),
      ...option(tool.intersection?.object instanceof Points === false, () => createPoint(tool)),
      ...option(!creatingLine, () => createTextAction(tool)),
      openCreatePointByCoordinateDialog(tool),
      openCreatePointByOffsetDialog(tool),
    ],
    action: () => {
      tool.contextMenuCallback?.();
    },
  };
};
